// src/pages/tryouts/TryoutsPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useTeam } from '@/context/TeamContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';

import {
  PlusIcon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  XMarkIcon,
  ChevronRightIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

const DEFAULT_CRITERIA = [
  { id: 'speed',     label: 'Speed / Athleticism',    maxScore: 10 },
  { id: 'technical', label: 'Technical Skills',        maxScore: 10 },
  { id: 'tactical',  label: 'Tactical Awareness',      maxScore: 10 },
  { id: 'physical',  label: 'Physical Strength',       maxScore: 10 },
  { id: 'attitude',  label: 'Attitude / Coachability', maxScore: 10 },
];

function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}

/* ─────────────────────────────────────────────
   Tryout card (white, lime hover)
───────────────────────────────────────────── */
function TryoutCard({ tryout }) {
  const navigate = useNavigate();
  const isOpen = tryout.status === 'open';
  const dateVal = toDate(tryout.date);

  return (
    <div
      className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 transition-colors cursor-pointer"
      onClick={() => navigate(`/tryouts/${tryout.id}`)}
    >
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3
              className="text-base font-bold uppercase tracking-tight text-[#0d1f14] truncate"
              style={{ fontFamily: HEAD_FONT }}
            >
              {tryout.name}
            </h3>
            <span
              className={clsx(
                'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shrink-0 border',
                isOpen
                  ? 'bg-[#b5f03d]/15 text-[#3d6010] border-[#b5f03d]/40'
                  : 'bg-zinc-50 text-zinc-500 border-zinc-200'
              )}
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              {isOpen ? 'Open' : 'Closed'}
            </span>
          </div>

          <div className="mt-2 flex flex-wrap gap-3 text-sm text-zinc-500">
            {dateVal && (
              <span className="flex items-center gap-1.5">
                <CalendarDaysIcon className="h-4 w-4 text-[#5a8a1f]" />
                <span className="text-zinc-700">
                  {format(dateVal, 'MMM d, yyyy')}
                  {tryout.time && ` at ${tryout.time}`}
                </span>
              </span>
            )}
            {tryout.location && (
              <span className="flex items-center gap-1.5">
                <MapPinIcon className="h-4 w-4 text-[#5a8a1f]" />
                <span className="text-zinc-700">{tryout.location}</span>
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <UserGroupIcon className="h-4 w-4 text-[#5a8a1f]" />
              <span className="text-zinc-700">
                {tryout.playerCount ?? 0} players
                {tryout.avgScore > 0 && ` · avg ${tryout.avgScore.toFixed(1)}`}
              </span>
            </span>
          </div>
        </div>

        <ChevronRightIcon className="h-5 w-5 text-zinc-300 shrink-0 mt-0.5" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main page
───────────────────────────────────────────── */
export default function TryoutsPage() {
  const { currentTeam } = useTeam();
  const navigate = useNavigate();

  const [tryouts, setTryouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '',
    date: '',
    time: '',
    location: '',
    notes: '',
  });

  // Live list
  useEffect(() => {
    if (!currentTeam?.id) {
      setTryouts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, 'tryouts'),
      where('teamId', '==', currentTeam.id),
      orderBy('date', 'desc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setTryouts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('[TryoutsPage]', err);
        setLoading(false);
      }
    );
    return unsub;
  }, [currentTeam?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.date) { toast.error('Date is required'); return; }

    setSaving(true);
    try {
      const dateTime = form.date ? new Date(form.date) : null;
      const ref = await addDoc(collection(db, 'tryouts'), {
        teamId: currentTeam.id,
        teamName: currentTeam.name,
        name: form.name.trim(),
        date: dateTime,
        time: form.time || '',
        location: form.location.trim(),
        notes: form.notes.trim(),
        status: 'open',
        playerCount: 0,
        createdAt: serverTimestamp(),
        evaluationCriteria: DEFAULT_CRITERIA,
      });
      toast.success('Tryout created!');
      setForm({ name: '', date: '', time: '', location: '', notes: '' });
      setShowForm(false);
      navigate(`/tryouts/${ref.id}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to create tryout');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">

        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p
              className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              Team
            </p>
            <h1
              className="mt-1 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              Tryouts
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Manage player tryout events for {currentTeam?.name ?? 'your team'}.
            </p>
          </div>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <PlusIcon className="h-4 w-4" />
              New Tryout
            </button>
          )}
        </div>

        {/* Inline create form */}
        {showForm && (
          <div className="mb-6 rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5">
            <div className="flex items-center justify-between mb-4">
              <h2
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                style={{ fontFamily: HEAD_FONT }}
              >
                New Tryout
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-zinc-400 hover:text-[#0d1f14] transition-colors"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. Spring 2025 Tryouts"
                  className="w-full rounded-xl border border-zinc-200 px-3 py-2.5 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                  required
                />
              </div>

              {/* Date + Time row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={form.date}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-zinc-200 px-3 py-2.5 text-sm text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Time
                  </label>
                  <input
                    type="time"
                    name="time"
                    value={form.time}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-zinc-200 px-3 py-2.5 text-sm text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  placeholder="e.g. Main Field, City Sports Complex"
                  className="w-full rounded-xl border border-zinc-200 px-3 py-2.5 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Any additional details..."
                  className="w-full rounded-xl border border-zinc-200 px-3 py-2.5 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  {saving ? 'Creating…' : 'Create Tryout'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map((n) => (
              <div key={n} className="h-24 rounded-2xl bg-zinc-100 animate-pulse" />
            ))}
          </div>
        ) : tryouts.length === 0 ? (
          <div className="rounded-2xl border border-zinc-200 bg-white p-10 sm:p-12 text-center shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
              <ClipboardDocumentCheckIcon className="h-7 w-7 text-[#5a8a1f]" />
            </div>
            <h3
              className="text-lg font-bold uppercase tracking-wide text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              No tryouts yet
            </h3>
            <p className="mt-2 text-sm text-zinc-500">
              Create your first tryout to start evaluating players.
            </p>
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                <PlusIcon className="h-4 w-4" />
                Create First Tryout
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {tryouts.map((t) => (
              <TryoutCard key={t.id} tryout={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
